import React, {ChangeEvent, useEffect, useState} from "react";
import {SysPrefListing} from "./model/SysPrefListing";
import {
  AmsDataGrid,
  AmsDataGridPageModel,
  AmsDataGridSortItem,
  DEFAULT_PAGE_SIZE
} from "../framework/component/data-grid/AmsDataGrid";
import {AxiosResponse} from "axios";
import {
  FilterCondition,
  getApiSort,
  requestQuery,
  ServerResponse,
  translateToQueryApiOperator,
  typeOperatorMap
} from "../framework/QueryApi";
import {Box} from "@mui/material";
import Accordion from "react-bootstrap/esm/Accordion";
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";
import Form from "react-bootstrap/esm/Form";
import {Button} from "react-bootstrap";
import {sysPrefListColumns} from "./model/SysPrefListColumns";
import {
  GridColumnVisibilityModel
} from "@mui/x-data-grid/hooks/features/columns/gridColumnsInterfaces";

const fieldOptions = ['ID', 'Preference', 'Category', 'Value', 'Startup Required', 'Default Value', 'Help Text', 'Data Type'];

//  TODO  Move to Filter UI component when created
type Filter = {
  field: string;
  operator: string;
  value: string;
};

const fields = new Map<string, keyof SysPrefListing>([
  ['ID', 'prefName'],
  ['Preference', 'displayName'],
  ['Category', 'category'],
  ['Value', 'prefValue'],
  ['Startup Required', 'startupRequired'],
  ['Default Value', 'defaultValue'],
  ['Help Text', 'helpText'],
  ['Data Type', 'dataType']
]);

function buildApiFilter(filters: Filter[]) {
  return filters
  .filter(f => f.field && f.operator && f.value)
  .map(f => {
    const apiFieldCode = fields.get(f.field);
    const apiOperator = translateToQueryApiOperator(f.operator);
    if (!apiFieldCode || !apiOperator) {
      throw Error(`Unknown field or operator: ${f.field}, ${f.operator}`);
    } else {
      return new FilterCondition(apiFieldCode, apiOperator, f.value)
    }
  });
}

const INITIAL_FILTER: Filter = {field: "", operator: "", value: ""};
const INITIAL_SORT = [{field: "prefName", direction: "asc"}];
const DEFAULT_COLUMN_VISIBILITY: GridColumnVisibilityModel = {
  prefName: false,
  helpText: false,
  dataType: false,
}
export default function SysPrefsList() {
  const [rows, setRows] = useState<SysPrefListing[]>([]);
  const [filters, setFilters] = useState<Filter[]>([INITIAL_FILTER]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<AmsDataGridSortItem[]>(INITIAL_SORT);


  const addFilter = () => {
    setFilters([...filters, {field: '', operator: '', value: ''}]);
  };

  const removeFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleFilterChange = (index: number, key: keyof Filter, value: string) => {
    filters[index][key] = value;
    setFilters(filters.slice());
  };

  const handlePaginationChange = (model: AmsDataGridPageModel) => {
    setPageNo(model.page)
    setPageSize(model.pageSize)
  };

  const handleSortChange = (model: AmsDataGridSortItem[]) => {
    setSort(model);
  };

  const populateDataGrid = (response: AxiosResponse) => {
    const data: ServerResponse = response.data;

    setRows(data.data);
    setRowCount(data.meta.totalRecords);
    setLoading(false);

  };

  const fetchData = () => {
    setLoading(true);
    requestQuery('sysPrefListing', buildApiFilter(filters), [], getApiSort(sort), populateDataGrid, pageNo, pageSize)
    .catch(e => console.error("Failed to fetch sys pref listing", e));
    //TODO ^ this error should be displayed to the end user
  }

  const handleSearch = () => {
    fetchData();
  };

  useEffect(() => {
    fetchData();
  }, [sort, pageNo, pageSize]);

  return (
      <Box sx={{height: 500, width: '100%'}}>
        <Accordion defaultActiveKey="0">
          <Accordion.Item eventKey="0">
            <Accordion.Header>System Preferences Search</Accordion.Header>
            <Accordion.Body>

              <Row className="mb-1 align-items-end">
                <Col md={1}></Col>
                <Col md={3}>Field</Col>
                <Col md={2}>Operator</Col>
                <Col md={3}>Value</Col>
                <Col md={2}></Col>
              </Row>
              {filters.map((filter: Filter, index) => (
                  <Row key={index} className="mb-3 align-items-end">
                    <Col md={1}>
                      <Row><Col className={"text-end"}><i className="h3 bi bi-plus text-success"
                                                          role={"button"}
                                                          onClick={addFilter}></i>
                        <i className="h3 bi bi-x text-danger" role={"button"}
                           onClick={() => removeFilter(index)}></i></Col>
                      </Row>
                    </Col>
                    <Col md={3}>
                      <Form.Select
                          value={filter.field}
                          onChange={(e: ChangeEvent<HTMLSelectElement>) => handleFilterChange(index, "field", e.target.value)}>
                        <option value="">Select Field</option>
                        {fieldOptions.map((field) => (
                            <option key={field} value={field}>
                              {field}
                            </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Select
                          value={filter.operator}
                          onChange={(e: ChangeEvent<HTMLSelectElement>) => handleFilterChange(index, "operator", e.target.value)}>
                        <option value="">Select Operator</option>
                        {(typeOperatorMap.get(sysPrefListColumns.find(c =>
                            c.field == fields.get(filter.field))?.type ?? '') ?? [])
                        .map((op) => (
                            <option key={op} value={op}>
                              {op}
                            </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Form.Control
                          type="text"
                          placeholder="Enter value"
                          value={filter.value}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleFilterChange(index, "value", e.target.value)}/>
                    </Col>
                  </Row>
              ))}
              {filters.length == 0 &&
                  <Button variant="primary" className={"mt-4"} onClick={addFilter}>Add a
                    Filter</Button>
              }

            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        <div className={"text-center"}><Button variant="primary" className={"mt-3 mb-3"}
                                               onClick={handleSearch}>Search</Button></div>
        <div style={{display: 'flex', flexDirection: 'column', maxHeight: 600, minHeight: 400}}>
          <AmsDataGrid
              rows={rows}
              rowIdFunction={(row: SysPrefListing) => row.prefName}
              columnModel={sysPrefListColumns}
              rowCount={rowCount}
              paginationChangeFunction={handlePaginationChange}
              sortChangeFunction={handleSortChange}
              loadingFlag={loading}
              initialSort={INITIAL_SORT}
              initialState={{
                columns: {
                  columnVisibilityModel: DEFAULT_COLUMN_VISIBILITY
                },
              }
              }
          />
        </div>
      </Box>
  );
}